import {
  type ButtonClick,
  type Entity,
  type LinkClick,
  type SportsBet,
  type TrackingContextValue,
  buttonClick,
  errorView,
  linkClick,
  sportsBet,
  sportsCompetition,
  sportsEvent,
  sportsMarket,
  sportsRace,
  sportsSelection,
  sportsSport,
  widgetView
} from '@ps/web-core-analytics';
import type { Currency } from '@ps/web-core-store';
import { isEqual, merge } from 'lodash';

import { GAEvent, getEntities } from '@swf/core/analytics/utils';
import { GADimension } from '@swf/core/enums';
import type { RequestError } from '@swf/core/errors/RequestError';
import type { BettableOpportunities } from '@swf/core/models/sports/types/betSlip';
import {
  getIsAvailableOpportunity,
  getIsSingleAvailableOpportunity
} from '@swf/core/models/sports/utils/betSlip/getIsOpportunity';
import { RunnerStatusMarketDetails } from '@swf/generated/types/api/sports';
import type {
  CompetitionId,
  EventId,
  MarketId,
  MarketType,
  MeetingId,
  Odds,
  OpportunityStatus,
  RaceId,
  RunnerId,
  SelectionId,
  SingleAvailableOpportunity,
  SingleOpportunity
} from '@swf/generated/types/api/sports';
import type { EventTypeId } from '@swf/sports/types/eventType';
import type { BetDefinition, PlaceBetResp, PlaceBetResult } from '@swf/sports/types/place-bet';
import getRunnerStatus from '@swf/sports/utils/getRunnerStatus';
import isOddsBoostMarket from '@swf/sports/utils/isOddsBoostMarket';
import mergeWithTrackingContextValue from '@swf/sports/utils/mergeWithTrackingContextValue';

import {
  createMultipleEntities,
  createSingleEntities
} from '../../../widgets/sports-bet-slip-widget/utils/createEntities';

export type BetslipTrackingArg = ButtonClick & { entities: Entity[] };

interface AddedToBetslipProps {
  eventTypeId: EventTypeId;
  eventId: EventId;
  startTime: string;
  marketId: MarketId;
  selectionId: SelectionId;
  odds?: Odds;
  runnerStatus?: RunnerStatusMarketDetails;
  competitionId?: CompetitionId;
  marketType?: MarketType;
}

export const addedToBetslip = (
  context: TrackingContextValue,
  {
    eventId,
    eventTypeId,
    competitionId,
    startTime,
    marketId,
    selectionId,
    odds,
    runnerStatus,
    marketType
  }: AddedToBetslipProps
) => {
  const entities: Entity[] = [
    sportsEvent({ eventId, eventStartTime: new Date(startTime).getTime() }),
    sportsMarket({
      marketId,
      marketInPlay: false,
      marketIsOddsBoost: isOddsBoostMarket(marketType)
    }),
    sportsSelection({
      selectionId,
      selectionOdds: odds?.decimalDisplayOdds?.decimalOdds,
      selectionStatus: runnerStatus || RunnerStatusMarketDetails.ACTIVE,
      isStartingPrice: !odds?.decimalDisplayOdds?.decimalOdds
    }),
    sportsSport({ sportId: eventTypeId })
  ];

  if (competitionId) {
    entities.push(sportsCompetition({ competitionId }));
  }

  context = mergeWithTrackingContextValue(context, entities);

  GAEvent({ action: 'Add Selection' }, context);
  buttonClick({ buttonName: 'Add Selection' }, context);
};

export type BetslipClearedProps = SingleOpportunity[];
export const betslipCleared = (
  context: TrackingContextValue,
  singleOpportunities: BetslipClearedProps
) => {
  const entities: Entity[] = singleOpportunities.map((single) => {
    const selectionOdds = getIsAvailableOpportunity(single)
      ? single.winRunnerOdds?.decimalDisplayOdds?.decimalOdds
      : undefined;

    const selectionEntity = sportsSelection({
      selectionId: single.runnerId.selectionId,
      selectionOdds,
      selectionStatus: getRunnerStatus(single.status as OpportunityStatus),
      isStartingPrice: !selectionOdds
    });

    return selectionEntity;
  });

  const clearBetSlipContext = {
    category: context.category,
    widget: context.widget,
    entities: [...getEntities(context), ...entities]
  };

  GAEvent({ action: 'Clear Bet Slip' }, clearBetSlipContext);
  buttonClick({ buttonName: 'Clear Bet Slip' }, clearBetSlipContext);
};

export interface SelectionRemovedProps {
  singleOpportunities: SingleOpportunity[];
  runnerId: RunnerId;
}
export const selectionRemoved = (
  context: TrackingContextValue,
  { singleOpportunities, runnerId }: SelectionRemovedProps
) => {
  const runnerStatusMap: Record<OpportunityStatus, RunnerStatusMarketDetails> = {
    AVAILABLE: RunnerStatusMarketDetails.ACTIVE,
    SUSPENDED: RunnerStatusMarketDetails.SUSPENDED,
    UNAVAILABLE: RunnerStatusMarketDetails.REMOVED,
    RESTRICTED: RunnerStatusMarketDetails.ACTIVE
  };
  const opportunity = singleOpportunities.find((opp) => isEqual(opp.runnerId, runnerId));
  if (opportunity) {
    const entities: Entity[] = [
      sportsEvent({
        eventId: opportunity.eventId,
        eventStartTime: getIsSingleAvailableOpportunity(opportunity)
          ? new Date(opportunity.eventStartTime).getTime()
          : undefined
      }),
      sportsMarket({
        marketId: runnerId.marketId,
        marketInPlay: getIsAvailableOpportunity(opportunity) ? opportunity.eventInPlay : false,
        marketIsOddsBoost: getIsAvailableOpportunity(opportunity)
          ? isOddsBoostMarket(opportunity.marketType)
          : false
      }),
      sportsSelection({
        selectionId: runnerId.selectionId,
        selectionOdds: getIsAvailableOpportunity(opportunity)
          ? opportunity.winRunnerOdds?.decimalDisplayOdds?.decimalOdds
          : undefined,
        selectionStatus: runnerStatusMap[opportunity.status],
        isStartingPrice: false
      })
    ];

    const { eventTypeId, competitionId } = opportunity;

    if (eventTypeId) {
      entities.push(sportsSport({ sportId: eventTypeId }));
    }

    if (competitionId) {
      entities.push(sportsCompetition({ competitionId }));
    }
    context = mergeWithTrackingContextValue(context, entities);

    GAEvent({ action: 'Remove Selection' }, context);
    buttonClick({ buttonName: 'Remove Selection' }, context);
  }
};

const createSportsBetEntity = (
  result: PlaceBetResult,
  betDefinitions: BetDefinition[],
  currency: Currency,
  bettableOpportunities: BettableOpportunities
) => {
  const { betId, betNo, totalStake, totalPotentialWin, betPlacedTime, runners } = result;

  if (!betId || !totalStake || !betPlacedTime || !runners) {
    return null;
  }

  const betType =
    betDefinitions.find((betDefinition) => betDefinition.betNo === betNo)?.betType || 'UNKNOWN';

  const matchingOpportunityId =
    betDefinitions.find((betDefinition) => betDefinition.betNo === betNo)?.id || 'UNKNOWN';

  const placedBetOpportunity = bettableOpportunities.MULTIPLE.find(
    (bettableOpportunity) => bettableOpportunity.id === matchingOpportunityId
  );

  let betCategory: SportsBet['betCategory'] = 'STANDARD';

  const features: string[] = placedBetOpportunity?.features ?? [];

  if (features.includes('SGM_MULTIPLES')) {
    betCategory = 'BET_BUILDER_PLUS';
  } else if (features.includes('SGM')) {
    betCategory = 'BET_BUILDER';
  }

  return sportsBet({
    betCurrency: currency as SportsBet['betCurrency'],
    betType: betType as SportsBet['betType'],
    betId,
    betStake: totalStake,
    betPotentialReturn: totalPotentialWin,
    betPlacedTime: new Date(betPlacedTime).getTime(),
    betCashoutAvailable: false,
    betCashedOut: false,
    isStartingPrice: !totalPotentialWin,
    betCategory
  });
};

interface BetPlacedProps {
  response: PlaceBetResp;
  betDefinitions: BetDefinition[];
  bettableOpportunities: BettableOpportunities;
  opportunities: SingleAvailableOpportunity[];
  currency: Currency;
}
export const betPlaced = (
  context: TrackingContextValue,
  { response, betDefinitions, bettableOpportunities, opportunities, currency }: BetPlacedProps
) => {
  const entities: Entity[] = [
    ...createSingleEntities(bettableOpportunities.SINGLE),
    ...createMultipleEntities(opportunities)(
      bettableOpportunities.MULTIPLE,
      response.result?.flatMap((result) => result.runners ?? [])
    )
  ];

  response.result?.forEach((result) => {
    const sportsBetEntity: Entity | null = createSportsBetEntity(
      result,
      betDefinitions,
      currency,
      bettableOpportunities
    );

    if (sportsBetEntity) entities.push(sportsBetEntity);
  });

  const betPlacedContext = {
    category: context.category,
    widget: merge(context.widget, { widgetContext: 'BET_RECEIPT' }),
    entities
  };

  GAEvent({ action: 'Bet placed' }, betPlacedContext);
  widgetView({}, betPlacedContext);
};

interface BetPlacedErrorProps {
  response: PlaceBetResp;
  bettableOpportunities: BettableOpportunities;
  opportunities: SingleAvailableOpportunity[];
}
export const betPlacementError = (
  context: TrackingContextValue,
  { response, bettableOpportunities, opportunities }: BetPlacedErrorProps
): void => {
  const entities: Entity[] = [
    ...createSingleEntities(bettableOpportunities.SINGLE),
    ...createMultipleEntities(opportunities)(bettableOpportunities.MULTIPLE)
  ];

  context.entities = entities;

  if (response.respCode) {
    GAEvent({ action: response.respCode }, context);
    errorView({ errorCode: response.respCode }, context);
  }

  response.result?.forEach((result) => {
    GAEvent({ action: result.resultCode }, context);
    errorView({ errorCode: result.resultCode }, context);

    result.runners?.forEach((runner) => {
      if (runner.failureCode) {
        GAEvent({ action: runner.failureCode }, context);
        errorView({ errorCode: runner.failureCode }, context);
      }
    });
  });
};

interface BetPlacedExceptionProps {
  exception: RequestError;
  bettableOpportunities: BettableOpportunities;
  opportunities: SingleAvailableOpportunity[];
}
export const betPlacementException = (
  context: TrackingContextValue,
  { exception, bettableOpportunities, opportunities }: BetPlacedExceptionProps
) => {
  context = mergeWithTrackingContextValue(context, [
    ...createSingleEntities(bettableOpportunities.SINGLE),
    ...createMultipleEntities(opportunities)(bettableOpportunities.MULTIPLE)
  ]);

  try {
    const parsedException = JSON.parse(exception.body.code);

    GAEvent(
      { action: `(${parsedException.faultstring}) ${parsedException.detail.message}` },
      context
    );

    errorView(
      { errorCode: `(${parsedException.faultstring}) ${parsedException.detail.message}` },
      context
    );
  } catch (e) {
    GAEvent({ action: 'Bet placement failed' }, context);
    errorView({ errorCode: 'Bet placement failed' }, context);
  }
};

export interface EventPageTabEntities {
  eventId: EventId;
  sportId: EventTypeId;
  competitionId: CompetitionId;
  eventStartTime: string;
}

export interface RacePageTabEntities {
  raceId: RaceId;
  sportId: EventTypeId;
  meetingId: MeetingId;
  eventStartTime: string;
}

interface TabClicked {
  entities: EventPageTabEntities | RacePageTabEntities;
  linkData: LinkClick;
}

export const isRaceBasedEntity = (
  entities: EventPageTabEntities | RacePageTabEntities
): entities is RacePageTabEntities => 'raceId' in entities;

export const marketTabClicked = (
  context: TrackingContextValue,
  { entities, linkData }: TabClicked
) => {
  const { destinationUrl, linkText, resolverTag } = linkData;

  if (isRaceBasedEntity(entities)) {
    const { raceId, sportId } = entities;
    const raceEntities: Entity[] = [sportsRace({ raceId }), sportsSport({ sportId })];

    context = mergeWithTrackingContextValue(context, raceEntities);

    GAEvent(
      { label: linkText ?? '', action: 'Hyperlink Pressed' },
      context,
      resolverTag ? { [GADimension.ResolverTag]: resolverTag } : {}
    );
    linkClick({ destinationUrl, linkText, resolverTag }, context);
  } else {
    const { eventId, sportId, competitionId, eventStartTime } = entities;

    const eventEntities: Entity[] = [
      sportsEvent({ eventId, eventStartTime: new Date(eventStartTime).getTime() }),
      sportsSport({ sportId })
    ];

    if (competitionId) {
      eventEntities.push(sportsCompetition({ competitionId }));
    }

    context = mergeWithTrackingContextValue(context, eventEntities);

    GAEvent(
      { label: linkText ?? '', action: 'Hyperlink Pressed' },
      context,
      resolverTag ? { [GADimension.ResolverTag]: resolverTag } : {}
    );
    linkClick({ destinationUrl, linkText, resolverTag }, context);
  }
};
